import type {
  DesktopPraxaIdentityBridge,
  PraxaDesktopAccountWorkspace,
  PraxaDesktopIdentitySnapshot,
  PraxaDesktopSignInResult,
} from "@praxa/platform-contracts";
import {
  Building2Icon,
  ChevronsUpDownIcon,
  CloudOffIcon,
  LoaderCircleIcon,
  LogInIcon,
  LogOutIcon,
  RefreshCwIcon,
  RotateCcwIcon,
  SettingsIcon,
  UserRoundIcon,
} from "lucide-react";
import { useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";

import { resolvePraxaSidebarPlanLabel } from "../../praxaPlanPresentation";
import { useDesktopPraxaAccountControls } from "../../state/desktopPraxaAccountControls";
import {
  type PraxaIdentityAction,
  settlePraxaIdentityAction,
  useDesktopPraxaIdentity,
} from "../../state/desktopPraxaIdentity";
import {
  Menu,
  MenuGroup,
  MenuGroupLabel,
  MenuItem,
  MenuPopup,
  MenuRadioGroup,
  MenuRadioItem,
  MenuSeparator,
  MenuTrigger,
} from "../ui/menu";
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem, useSidebar } from "../ui/sidebar";

type PraxaActionResult = PraxaDesktopIdentitySnapshot | PraxaDesktopSignInResult;
const WORKSPACE_TRIGGER_PRESENTATION = { kind: "workspace" } as const;

export interface PraxaWorkspaceAccountMenuContentProps {
  readonly applySnapshot: (snapshot: PraxaDesktopIdentitySnapshot) => void;
  readonly bridge: DesktopPraxaIdentityBridge;
  readonly loadError: string | null;
  readonly loading: boolean;
  readonly retry: () => Promise<void>;
  readonly state: PraxaDesktopIdentitySnapshot | null;
  readonly openAccountSettings?: () => void;
  readonly triggerPresentation?:
    | { readonly kind: "workspace" }
    | { readonly kind: "account"; readonly planLabel: string };
}

function activeWorkspace(state: PraxaDesktopIdentitySnapshot): PraxaDesktopAccountWorkspace | null {
  return (
    state.workspaces.find((workspace) => workspace.workspaceId === state.activeWorkspaceId) ?? null
  );
}

export function resolvePraxaAccountLabel(state: PraxaDesktopIdentitySnapshot | null): string {
  if (state === null) return "Loading Praxa account…";
  if (state.status === "signed-out") return "Sign in to Praxa Control";
  if (state.status === "signing-in") return "Signing in to Praxa…";
  if (state.status === "configuration-required") return "Praxa setup required";
  if (state.profile === null) {
    return state.status === "offline" ? "Praxa is offline" : "Reconnect Praxa";
  }
  return activeWorkspace(state)?.name ?? state.profile.displayName;
}

export function resolvePraxaSidebarAccountName(state: PraxaDesktopIdentitySnapshot): string {
  const username = state.account?.profile.username?.trim();
  if (username) return `@${username}`;

  const accountDisplayName = state.account?.profile.displayName.trim();
  return accountDisplayName || state.profile?.displayName || resolvePraxaAccountLabel(state);
}

export function resolvePraxaAccountInitials(displayName: string): string {
  const words = displayName.trim().split(/\s+/u).filter(Boolean);
  const first = Array.from(words[0] ?? "")[0] ?? "?";
  const last = words.length > 1 ? (Array.from(words.at(-1) ?? "")[0] ?? "") : "";
  return `${first}${last}`.toLocaleUpperCase();
}

function PraxaAccountAvatar({
  avatarUrl,
  displayName,
}: {
  readonly avatarUrl: string | null;
  readonly displayName: string;
}) {
  return (
    <span
      className="relative flex size-6 shrink-0 items-center justify-center overflow-hidden rounded-full border border-sidebar-border bg-sidebar-control-surface text-[10px] font-semibold leading-none text-sidebar-foreground"
      data-sidebar-account-avatar=""
    >
      {resolvePraxaAccountInitials(displayName)}
      {avatarUrl ? (
        <img
          key={avatarUrl}
          alt=""
          className="absolute inset-0 size-full object-cover"
          onError={(event) => {
            event.currentTarget.hidden = true;
          }}
          src={avatarUrl}
        />
      ) : null}
    </span>
  );
}

function WorkspaceIcon({ workspace }: { readonly workspace: PraxaDesktopAccountWorkspace | null }) {
  return workspace?.kind === "organization" ? (
    <Building2Icon className="size-4 shrink-0" />
  ) : (
    <UserRoundIcon className="size-4 shrink-0" />
  );
}

export function PraxaIdentityStatusMessage({
  message,
  tone = "error",
}: {
  readonly message: string | null;
  readonly tone?: "error" | "muted";
}) {
  if (!message) return null;

  return (
    <p
      aria-atomic="true"
      aria-live="polite"
      className={`px-2 text-xs leading-4 ${
        tone === "error" ? "text-destructive" : "text-muted-foreground"
      }`}
      role="status"
    >
      {message}
    </p>
  );
}

export function PraxaWorkspaceAccountMenuContent({
  applySnapshot,
  bridge,
  loadError,
  loading,
  retry,
  state,
  openAccountSettings,
  triggerPresentation = WORKSPACE_TRIGGER_PRESENTATION,
}: PraxaWorkspaceAccountMenuContentProps) {
  const [actionPending, setActionPending] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    setActionError(null);
  }, [state]);

  const invoke = async (
    actionName: PraxaIdentityAction,
    action: () => Promise<PraxaActionResult>,
  ) => {
    setActionPending(true);
    setActionError(null);
    try {
      const result = await settlePraxaIdentityAction(actionName, action);
      if (result.snapshot) applySnapshot(result.snapshot);
      setActionError(result.error);
    } finally {
      setActionPending(false);
    }
  };

  if (state === null) {
    return (
      <SidebarMenu>
        <SidebarMenuItem>
          <SidebarMenuButton
            aria-label={loadError ? "Retry Praxa account" : "Loading Praxa account"}
            className="min-h-8 items-center gap-2 rounded-md px-2 py-1.5 text-sm font-medium text-sidebar-muted-foreground/80 hover:bg-sidebar-row-hover hover:text-sidebar-foreground disabled:opacity-70"
            data-sidebar-account-primary=""
            disabled={loading || !loadError}
            onClick={() => (loadError ? void retry() : undefined)}
            size="sm"
          >
            {loading || !loadError ? (
              <LoaderCircleIcon className="size-4 shrink-0 animate-spin" />
            ) : (
              <RefreshCwIcon className="size-4 shrink-0" />
            )}
            <span className="truncate">
              {loadError ? "Retry Praxa account" : "Loading Praxa account…"}
            </span>
          </SidebarMenuButton>
          <PraxaIdentityStatusMessage message={loadError} />
        </SidebarMenuItem>
      </SidebarMenu>
    );
  }

  const label = resolvePraxaAccountLabel(state);

  if (state.status === "signed-out") {
    return (
      <SidebarMenu>
        <SidebarMenuItem>
          <SidebarMenuButton
            aria-label={label}
            className="min-h-8 items-center gap-2 rounded-md px-2 py-1.5 text-sm font-medium text-sidebar-muted-foreground/80 hover:bg-sidebar-row-hover hover:text-sidebar-foreground"
            data-sidebar-account-primary=""
            disabled={actionPending}
            onClick={() => void invoke("beginSignIn", () => bridge.beginSignIn())}
            size="sm"
          >
            <LogInIcon className="size-4 shrink-0" />
            <span className="truncate">{label}</span>
          </SidebarMenuButton>
          <PraxaIdentityStatusMessage
            message={actionError ?? state.message}
            tone={actionError ? "error" : "muted"}
          />
        </SidebarMenuItem>
      </SidebarMenu>
    );
  }

  if (state.status === "signing-in") {
    return (
      <SidebarMenu>
        <SidebarMenuItem>
          <SidebarMenuButton
            aria-label="Cancel Praxa sign-in"
            className="min-h-8 items-center gap-2 rounded-md px-2 py-1.5 text-sm font-medium text-sidebar-muted-foreground/80 hover:bg-sidebar-row-hover hover:text-sidebar-foreground disabled:opacity-70"
            data-sidebar-account-primary=""
            disabled={actionPending}
            onClick={() => void invoke("cancelSignIn", () => bridge.cancelSignIn())}
            size="sm"
            title="Cancel Praxa sign-in and keep working locally."
          >
            <LoaderCircleIcon className="size-4 shrink-0 animate-spin" />
            <span className="min-w-0 flex-1 truncate">{label}</span>
            <span className="shrink-0 text-xs">Cancel</span>
          </SidebarMenuButton>
          <PraxaIdentityStatusMessage
            message={
              actionError ??
              state.message ??
              "Finish sign-in in your browser, or cancel to keep working locally."
            }
            tone={actionError ? "error" : "muted"}
          />
        </SidebarMenuItem>
      </SidebarMenu>
    );
  }

  if (state.status === "configuration-required") {
    return (
      <SidebarMenu>
        <SidebarMenuItem>
          <SidebarMenuButton
            aria-label="Reset Praxa session"
            className="min-h-8 items-center gap-2 rounded-md px-2 py-1.5 text-sm font-medium text-sidebar-muted-foreground/80 hover:bg-sidebar-row-hover hover:text-sidebar-foreground disabled:opacity-70"
            data-sidebar-account-primary=""
            disabled={actionPending}
            onClick={() => void invoke("resetLocalSession", () => bridge.resetLocalSession())}
            size="sm"
            title="Remove this device's saved Praxa sign-in so you can reconnect."
          >
            <RotateCcwIcon className="size-4 shrink-0" />
            <span className="truncate">Reset Praxa session</span>
          </SidebarMenuButton>
          <PraxaIdentityStatusMessage
            message={
              actionError ??
              state.message ??
              "Praxa's saved session needs to be reset. Local coding is still available."
            }
          />
        </SidebarMenuItem>
      </SidebarMenu>
    );
  }

  if (state.profile === null) {
    const statusMessage =
      actionError ??
      state.message ??
      (state.status === "offline"
        ? "Praxa cloud is offline. Local coding is still available."
        : state.status === "error"
          ? "Praxa cloud could not reconnect. Local coding is still available."
          : null);

    return (
      <SidebarMenu>
        <SidebarMenuItem>
          <SidebarMenuButton
            aria-label={label}
            className="min-h-8 items-center gap-2 rounded-md px-2 py-1.5 text-sm font-medium text-sidebar-muted-foreground/80 hover:bg-sidebar-row-hover hover:text-sidebar-foreground disabled:opacity-70"
            data-sidebar-account-primary=""
            disabled={actionPending || !state.canRetry}
            onClick={() =>
              state.canRetry ? void invoke("refresh", () => bridge.refresh()) : undefined
            }
            size="sm"
            title={statusMessage ?? label}
          >
            {actionPending ? (
              <LoaderCircleIcon className="size-4 shrink-0 animate-spin" />
            ) : state.canRetry ? (
              <RefreshCwIcon className="size-4 shrink-0" />
            ) : (
              <CloudOffIcon className="size-4 shrink-0" />
            )}
            <span className="truncate">{label}</span>
          </SidebarMenuButton>
          <SidebarMenuButton
            aria-label="Remove saved Praxa session"
            className="min-h-8 items-center gap-2 rounded-md px-2 py-1.5 text-sm font-medium text-sidebar-muted-foreground/80 hover:bg-sidebar-row-hover hover:text-sidebar-foreground disabled:opacity-70"
            disabled={actionPending}
            onClick={() => void invoke("resetLocalSession", () => bridge.resetLocalSession())}
            size="sm"
            title="Remove this device's saved Praxa sign-in and keep working locally."
          >
            <RotateCcwIcon className="size-4 shrink-0" />
            <span className="truncate">Remove saved Praxa session</span>
          </SidebarMenuButton>
          <PraxaIdentityStatusMessage
            message={statusMessage}
            tone={actionError || state.status === "error" ? "error" : "muted"}
          />
        </SidebarMenuItem>
      </SidebarMenu>
    );
  }

  const currentWorkspace = activeWorkspace(state);
  const profile = state.profile;
  const accountProfile = state.account.profile;
  const accountDisplayName = accountProfile.displayName.trim() || profile.displayName;
  const accountAvatarUrl = profile.avatarUrl?.trim() || accountProfile.avatarUrl?.trim() || null;
  const accountName = resolvePraxaSidebarAccountName(state);
  const busy = actionPending || loading || state.status === "switching";
  const statusMessage =
    actionError ??
    (state.status === "switching"
      ? (state.message ?? "Changing Praxa workspace…")
      : state.status === "offline"
        ? (state.message ?? "Praxa cloud is offline. Local coding is still available.")
        : state.status === "error"
          ? (state.message ?? "Praxa cloud could not reconnect. Local coding is still available.")
          : null);

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <Menu>
          <MenuTrigger
            render={
              triggerPresentation.kind === "account" ? (
                <button
                  className="flex h-10 w-full min-w-0 cursor-pointer items-center gap-2 overflow-hidden rounded-md px-1.5 text-left outline-none hover:bg-sidebar-row-hover focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-sidebar disabled:pointer-events-none disabled:opacity-60"
                  data-sidebar-account-trigger=""
                  data-sidebar-account-primary=""
                  disabled={busy}
                  type="button"
                />
              ) : (
                <SidebarMenuButton
                  aria-label={`Praxa workspace: ${label}`}
                  className="min-h-8 items-center gap-2 rounded-md px-2 py-1.5 text-sm font-medium text-sidebar-muted-foreground/80 hover:bg-sidebar-row-hover hover:text-sidebar-foreground"
                  disabled={busy}
                  size="sm"
                />
              )
            }
          >
            {triggerPresentation.kind === "account" ? (
              <>
                <PraxaAccountAvatar avatarUrl={accountAvatarUrl} displayName={accountDisplayName} />
                <span className="flex min-w-0 flex-1 flex-col justify-center">
                  <span className="truncate text-[13px] font-medium leading-4 text-sidebar-foreground">
                    {accountName}
                  </span>
                  <span className="truncate text-[11px] leading-[14px] text-sidebar-muted-foreground">
                    {triggerPresentation.planLabel}
                  </span>
                </span>
                {state.status === "offline" ? (
                  <CloudOffIcon aria-label="Praxa cloud offline" className="size-3.5 shrink-0" />
                ) : null}
                <span className="sr-only">Open account and workspace menu</span>
              </>
            ) : (
              <>
                <WorkspaceIcon workspace={currentWorkspace} />
                <span className="min-w-0 flex-1 truncate">{label}</span>
                {state.status === "offline" ? (
                  <CloudOffIcon aria-label="Praxa cloud offline" className="size-3.5 shrink-0" />
                ) : null}
                <ChevronsUpDownIcon className="size-3.5 shrink-0 opacity-60" />
              </>
            )}
          </MenuTrigger>
          <MenuPopup align="start" className="w-72" side="top">
            <MenuGroup>
              <MenuGroupLabel>Praxa account</MenuGroupLabel>
              <div className="px-2 pb-1">
                <p className="truncate text-sm font-medium text-foreground">
                  {profile.displayName}
                </p>
                {profile.email ? (
                  <p className="truncate text-xs text-muted-foreground">{profile.email}</p>
                ) : null}
                {state.message ? (
                  <p className="mt-1 text-xs text-muted-foreground">{state.message}</p>
                ) : null}
              </div>
            </MenuGroup>
            <MenuSeparator />
            <MenuGroup>
              <MenuGroupLabel>Work as</MenuGroupLabel>
              <MenuRadioGroup
                onValueChange={(workspaceId) =>
                  void invoke("selectWorkspace", () => bridge.selectWorkspace(workspaceId))
                }
                value={state.activeWorkspaceId ?? ""}
              >
                {state.workspaces.map((workspace) => (
                  <MenuRadioItem
                    disabled={busy || state.status === "offline"}
                    key={workspace.workspaceId}
                    value={workspace.workspaceId}
                  >
                    <span className="flex min-w-0 items-center gap-2">
                      <WorkspaceIcon workspace={workspace} />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate">{workspace.name}</span>
                        {workspace.kind === "organization" ? (
                          <span className="block truncate text-xs text-muted-foreground">
                            {workspace.membership.roleTemplate?.name ??
                              `${workspace.membership.authorityRole.slice(0, 1).toUpperCase()}${workspace.membership.authorityRole.slice(1)}`}
                          </span>
                        ) : null}
                      </span>
                    </span>
                  </MenuRadioItem>
                ))}
              </MenuRadioGroup>
            </MenuGroup>
            <MenuSeparator />
            {openAccountSettings ? (
              <MenuItem disabled={busy} onClick={openAccountSettings}>
                <SettingsIcon />
                Account settings
              </MenuItem>
            ) : null}
            {state.canRetry ? (
              <MenuItem
                disabled={busy}
                onClick={() => void invoke("refresh", () => bridge.refresh())}
              >
                <RefreshCwIcon />
                Reconnect Praxa
              </MenuItem>
            ) : null}
            <MenuItem
              disabled={busy}
              onClick={() => void invoke("signOut", () => bridge.signOut())}
            >
              <LogOutIcon />
              Sign out of Praxa
            </MenuItem>
          </MenuPopup>
        </Menu>
        <PraxaIdentityStatusMessage
          message={statusMessage}
          tone={actionError || state.status === "error" ? "error" : "muted"}
        />
      </SidebarMenuItem>
    </SidebarMenu>
  );
}

export function PraxaWorkspaceAccountMenu() {
  const openAccountSettings = useOpenPraxaAccountSettings();
  const identity = useDesktopPraxaIdentity();
  if (!identity.bridge) return null;

  return (
    <PraxaWorkspaceAccountMenuContent
      {...identity}
      bridge={identity.bridge}
      openAccountSettings={openAccountSettings}
    />
  );
}

export function PraxaSidebarAccountFallback({
  openAccountSettings,
}: {
  readonly openAccountSettings: () => void;
}) {
  return (
    <button
      className="flex h-10 w-full min-w-0 cursor-pointer items-center gap-2 overflow-hidden rounded-md px-1.5 text-left outline-none hover:bg-sidebar-row-hover focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-sidebar"
      data-sidebar-account-fallback=""
      data-sidebar-account-primary=""
      onClick={openAccountSettings}
      type="button"
    >
      <PraxaAccountAvatar avatarUrl={null} displayName="Praxa" />
      <span className="flex min-w-0 flex-1 flex-col justify-center">
        <span className="truncate text-[13px] font-medium leading-4 text-sidebar-foreground">
          Praxa account
        </span>
        <span className="truncate text-[11px] leading-[14px] text-sidebar-muted-foreground">
          Plan unavailable
        </span>
      </span>
      <span className="sr-only">Open settings</span>
    </button>
  );
}

export function PraxaSidebarAccountMenu() {
  const openAccountSettings = useOpenPraxaAccountSettings();
  const identity = useDesktopPraxaIdentity();
  const accountControls = useDesktopPraxaAccountControls();
  if (!identity.bridge) {
    return <PraxaSidebarAccountFallback openAccountSettings={openAccountSettings} />;
  }

  const accountId = identity.state?.account?.accountId;
  const planLabel =
    accountId === undefined || accountId === null
      ? "Plan unavailable"
      : resolvePraxaSidebarPlanLabel({
          accountId,
          controls: accountControls.state,
          loading: accountControls.loading,
        });

  return (
    <PraxaWorkspaceAccountMenuContent
      {...identity}
      bridge={identity.bridge}
      openAccountSettings={openAccountSettings}
      triggerPresentation={{ kind: "account", planLabel }}
    />
  );
}

function useOpenPraxaAccountSettings(): () => void {
  const navigate = useNavigate();
  const { isMobile, setOpenMobile } = useSidebar();
  return useCallback(() => {
    if (isMobile) {
      setOpenMobile(false);
    }
    void navigate({ to: "/settings/account" });
  }, [isMobile, navigate, setOpenMobile]);
}
